import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { EmblemType, TeamSectionItemType } from 'webapp/enums/enums';
import { NotificationCreateType } from 'webapp/enums/notifications';
import { TrackingEvent } from 'webapp/enums/tracking';

import type BatchSelect from 'webapp/models-custom/batch-select';
import type Report from 'webapp/models-custom/report';
import type TeamSectionItem from 'webapp/models-custom/team-section-item';

import type { TrackedEvent } from 'webapp/services/analytics';
import type {
	TrackingOriginForTemplateCreated,
	TrackingOriginTypeForPreview,
	TrackingOriginTypeForSchedulePopup,
	TrackingOriginTypeForShare,
} from 'webapp/services/model-manager/reports';
import type { Action as TeamAction } from 'webapp/services/team';

interface Args {
	model: Report;
	hideSort?: boolean;
	batchSelect?: BatchSelect<Report>;
	trackingOrigin?: TrackingOriginTypeForSchedulePopup;
	isVisibleOnScreen?: boolean;
	teamSectionItem?: TeamSectionItem;
	removeFromSection?: (item: TeamSectionItem) => void;
	isRestricted?: boolean;
	checkVisibility?: boolean;
	onPermanentDelete?: () => void;
	onRestore?: () => void;
}

export type Action =
	| 'present'
	| 'presentWithNotes'
	| 'view'
	| 'edit'
	| 'share'
	| 'createNotification'
	| 'openAccountTemplateEditPopup'
	| 'openPublicTemplateEditPopup'
	| 'addToTeam'
	| 'openActivityLog'
	| 'toggleFavorite'
	| 'verify'
	| 'duplicate'
	| 'delete'
	| TeamAction
	| 'deletePermanently'
	| 'restore';
export type Option = PopupListItem<Action>;

export default class ReportsIndexItemBaseComponent extends Component<Args> {
	@service('request') declare requestService: Service.Request;
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('team') declare teamService: Service.Team;
	@service('model-manager/team-sections')
	declare mmTeamSectionsService: Service.ModelManager.TeamSections;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	@tracked isVisibleOnScreen = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.args.isVisibleOnScreen) {
			this.isVisibleOnScreen = true; // used on reports home page to always show grid item
		}
	}

	get trackingOrigin(): TrackingOriginTypeForSchedulePopup {
		return this.args.trackingOrigin || 'reports';
	}

	get trackingObj(): TrackedEvent {
		return {
			event: TrackingEvent.REPORT_EDITED,
			data: { origin: this.args.trackingOrigin },
		};
	}

	get emblemTypeName() {
		const model = this.args.model;

		if (model.model.newBadge) {
			return EmblemType.NEW;
		} else if (model.isPublicTemplate) {
			return EmblemType.PUBLIC_TEMPLATE;
		} else if (this.isDeleted) {
			return EmblemType.RECENTLY_DELETED;
		}

		return;
	}

	get options(): Option[] {
		const model = this.args.model,
			options: Option[] = [],
			isTeamSectionItem = this.isTeamSectionItem;

		if (this.isDeleted) {
			if (this.args.model.canCurrentUserEditWithoutDeleted) {
				options.push(
					{
						icon: 'Restore',
						text: 'Restore',
						action: 'restore',
					},
					{
						class: 'delete-permanently',
						text: 'Delete permanently',
						action: 'deletePermanently',
						icon: 'Trash',
					},
				);
			}
			return options;
		}

		options.push({
			icon: 'Databoards',
			text: 'View',
			action: 'view',
		});

		options.push({
			icon: 'Play',
			text: 'Present',
			action: 'present',
		});

		if (!model.isPageless) {
			options.push({
				icon: 'PresentNotes',
				text: 'Present with notes',
				action: 'presentWithNotes',
			});
		}

		if (model.canCurrentUserEdit) {
			options.push({
				icon: 'Pencil',
				text: 'Edit',
				action: 'edit',
			});

			const notificationsOption: PopupListItem<Action> | null =
				this.mmNotificationsService.getNotificationsDropdownOptionForFeature(
					this.args.model,
					'createNotification',
				);
			if (notificationsOption) {
				options.push(notificationsOption);
			}

			options.push({
				icon: 'Share',
				text: 'Share',
				action: 'share',
			});

			options.push({
				icon: 'FolderUpload',
				text: 'Save as template',
				action: 'openAccountTemplateEditPopup',
				extra: this.billingService.accountTemplatesEnabled
					? undefined
					: {
							type: 'featureLimitation',
						},
			});

			if (this.accountService.isPublictemplatesSpace) {
				options.push({
					icon: 'FolderUpload',
					text: `${model.isTemplate ? 'Edit' : 'Save as'} public template`,
					action: 'openPublicTemplateEditPopup',
				});
			}

			options.push({
				class: model.favorite ? 'accent' : '',
				icon: model.favorite ? 'StarFill' : 'Star',
				text: model.favorite ? 'Unfavorite' : 'Favorite',
				action: 'toggleFavorite',
			});

			options.push({
				icon: 'Verified',
				text: model.isVerified ? 'Unverify' : 'Verify',
				action: 'verify',
				isDisabled: !model.verificationInfo,
			});

			if (!isTeamSectionItem) {
				options.push({
					icon: 'Copy',
					text: 'Duplicate',
					action: 'duplicate',
				});
			}

			if (!isTeamSectionItem) {
				options.push({
					icon: 'Spaces',
					text: 'Add to Space',
					action: 'addToTeam',
				});
			}

			if (this.currentUserService.isAdmin) {
				options.push(this.mmActivityLogsService.getObjectForOptionsPopup());
			}

			options.push({
				class: 'delete',
				text: 'Delete',
				action: 'delete',
				icon: 'Trash',
			});
		}

		return options;
	}

	get isDemoAccount(): boolean {
		return this.currentUserService.isDemo;
	}

	get isSelected(): boolean {
		const { batchSelect, model } = this.args;
		return !!batchSelect?.isItemSelected(model);
	}

	get isTeamSectionItem(): boolean {
		return !!this.args.teamSectionItem && !!this.args.removeFromSection;
	}

	get teamSectionItemOptions(): Option[] {
		return this.teamService.getTeamSectionItemOptions(this.isTeamSectionItem);
	}

	get canCurrentUserEdit(): boolean {
		const canCurrentUserEdit = this.args.model.canCurrentUserEdit,
			teamSectionItem = this.args.teamSectionItem;

		return teamSectionItem
			? teamSectionItem.isCurrentUserMember && canCurrentUserEdit
			: canCurrentUserEdit;
	}

	get canCurrentUserEditWithoutDeleted() {
		const canCurrentUserEditWithoutDeleted = this.args.model.canCurrentUserEditWithoutDeleted,
			teamSectionItem = this.args.teamSectionItem;

		return teamSectionItem
			? teamSectionItem.isCurrentUserMember && canCurrentUserEditWithoutDeleted
			: canCurrentUserEditWithoutDeleted;
	}

	get showActions(): boolean {
		const teamSectionItem = this.args.teamSectionItem;
		return teamSectionItem ? teamSectionItem.isCurrentUserMember : true;
	}

	get isDeleted(): boolean {
		return !!this.args.model.deleted;
	}

	@action
	doOptionsAction(actionName: string, id?: string): void {
		this[actionName as Action](id);
	}

	@action
	changeOwner(): void {
		this.mmUsersService.openUserSelectPopup({
			title: 'Report owner',
			text: 'Select an owner for report:',
			currentUserId: String(this.args.model.ownerId),
			onSelect: (userId: string | null) => {
				const ownerId = typeof userId === 'string' ? +userId : userId;
				this.args.model.ownerId = ownerId;
				this.args.model
					.save()
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			},
		});
	}

	@action
	present() {
		this.mmReportsService.startPresent(this.args.model, 'reports');
	}

	@action
	presentWithNotes() {
		this.mmReportsService.startPresentWithNotes(this.args.model, 'reports');
	}

	@action
	view(): void {
		this.mmReportsService.openPreviewPopup(
			this.args.model,
			this.args.trackingOrigin as TrackingOriginTypeForPreview,
		);
	}

	@action
	edit(): void {
		this.mmReportsService.editReport(this.args.model);
	}

	@action
	share(): void {
		this.mmReportsService.openSharePopup(
			this.args.model,
			this.args.trackingOrigin as TrackingOriginTypeForShare,
		);
	}

	@action
	createNotification(type?: string) {
		this.mmNotificationsService.createNewForFeature(
			type as NotificationCreateType,
			this.args.model,
			'reports',
			true, // show the report/reminder tabs, same as the generic create flow
		);
	}

	@action
	openAccountTemplateEditPopup(): void {
		this.mmReportsService.openAccountTemplateEditPopup(
			this.args.model,
			this.trackingOrigin as TrackingOriginForTemplateCreated,
		);
	}

	@action
	openPublicTemplateEditPopup(): void {
		this.mmReportsService.openPublicTemplateEditPopup(this.args.model);
	}

	@action
	addToTeam() {
		const itemId = this.args.model.id;

		if (itemId) {
			this.mmTeamSectionsService.openAddPopup({
				objectId: itemId,
				objectType: TeamSectionItemType.REPORT,
			});
		}
	}

	@action
	openActivityLog() {
		this.mmActivityLogsService.createAndOpenPopup({
			model: this.args.model,
		});
	}

	@action
	toggleFavorite(): void {
		this.mmReportsService.toggleReportFavorite(this.args.model);
	}

	@action
	duplicate(): void {
		const duplicatePromise = this.mmReportsService.duplicateReport(this.args.model);

		if (duplicatePromise) {
			void duplicatePromise.then((createdReport) => {
				this.analyticsService.event(TrackingEvent.REPORT_CREATED, { origin: 'page' });
				this.mmReportsService.openPreviewPopup(createdReport);
			});
		}
	}

	@action
	delete(): void {
		this.args.batchSelect?.unselectAll();
		void this.mmReportsService.deleteReport(this.args.model);
	}

	@action
	selectItem(selected: boolean): void {
		const { model, batchSelect } = this.args;
		batchSelect?.changeSelection(model, selected);
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}

	@action
	removeFromSection() {
		const teamSectionItem = this.args.teamSectionItem;

		if (teamSectionItem) {
			this.args.removeFromSection?.(teamSectionItem);
		}
	}

	@action
	deletePermanently(): void {
		void this.mmReportsService.deleteReport(
			this.args.model,
			true,
			this.args.onPermanentDelete
				? () => {
						this.args.onPermanentDelete?.();
						return 0;
					}
				: undefined,
		);
	}

	@action
	restore(): void {
		const restorePromise = this.mmReportsService.restore(this.args.model);
		if (restorePromise) {
			this.args.onRestore?.();
		}
	}

	@action
	verify(): void {
		void this.mmReportsService.toggleVerification(this.args.model);
	}
}
